import { useEffect, useState } from 'react'
import type { CSSProperties } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useLocale } from '../i18n/useLocale'
import { loadSession } from '../auth/session'
import { myApiBase } from '../data/myApi'
import { confirmNearestCity, loadNearestCityPrompt } from '../screens/wizard/profileStore'

/*
 * "Is {city} your nearest city?" — one-tap prompt (Duncan ruling 2026-10-07).
 *
 * Imported legacy customers whose city was filled in by data cleaning carry
 * the server flag cityFilledIn (my-api V35, GET /me/profile). After sign-in,
 * when the flag is true AND a city is present, this card asks once:
 *   - Yes    → PUT /me/profile with the SAME city (at the read version); the
 *              server clears the flag; the card closes.
 *   - Change → the existing closed-list nearest-city picker: wizard step 3
 *              (residence + city) as an account-hub edit (`?hub=1`, the same
 *              entry the Profile edit hub's pencil uses). Saving a list city
 *              there clears the flag server-side.
 *   - X      → closes for THIS sign-in only; the flag stays, so the next
 *              sign-in asks again.
 *
 * SURFACE (least intrusive): a small card at the top of the four tab home
 * screens only (Get ready / Orders / Tickets / Profile) — never inside a
 * flow (wizard, waiver, heat, assign). Get ready redirects to Orders when
 * there is nothing to get ready for, so both homes carry it.
 *
 * "This sign-in" is the ID token's auth_time (Cognito keeps it across token
 * refreshes and sets a new one on every sign-in), stored per account. With no
 * auth_time the dismissal lasts for this page load only.
 */

/** Tab home routes the prompt may appear on (exact match). */
const HOME_PATHS = new Set(['/get-ready', '/orders', '/tickets', '/profile'])

/** The city picker: wizard step 3 as an account-hub edit. */
export const NEAREST_CITY_EDIT_PATH = '/get-ready/profile/step-3?hub=1'

const DISMISS_KEY = (sub: string) => `mytrumin.cityPrompt.dismissed.v1.${sub}`

/** In-memory dismissal (fallback when the token carries no auth_time, or
 *  storage is unavailable). Cleared by a full page load. */
let dismissedThisLoad = false

/** Test seam. */
export function resetNearestCityPromptForTests(): void {
  dismissedThisLoad = false
}

function signInMarker(): { sub: string; marker: string } | null {
  const s = loadSession()
  if (!s) return null
  const at = s.claims.auth_time
  return typeof at === 'number' || typeof at === 'string' ? { sub: s.claims.sub, marker: String(at) } : null
}

function isDismissed(): boolean {
  if (dismissedThisLoad) return true
  const m = signInMarker()
  if (!m) return false
  try {
    return localStorage.getItem(DISMISS_KEY(m.sub)) === m.marker
  } catch {
    return false
  }
}

function rememberDismissed(): void {
  dismissedThisLoad = true
  const m = signInMarker()
  if (!m) return
  try {
    localStorage.setItem(DISMISS_KEY(m.sub), m.marker)
  } catch {
    /* storage unavailable — the in-memory flag covers this page load */
  }
}

const cardStyle: CSSProperties = {
  position: 'relative', display: 'flex', flexDirection: 'column', gap: 10,
  border: '1px solid var(--hairline-12)', borderRadius: 'var(--r-card, 16px)',
  padding: '14px 44px 14px 16px', margin: '0 0 14px', maxWidth: 'var(--content-max)',
  background: 'var(--surface)', fontSize: 13,
}
const kickerStyle: CSSProperties = {
  fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: '.1em',
  textTransform: 'uppercase', color: 'var(--text-faint)', margin: 0,
}
const btnBase: CSSProperties = {
  minHeight: 'var(--tap-min)', borderRadius: 9, padding: '0 16px', fontFamily: 'inherit',
  fontSize: 13, fontWeight: 800, cursor: 'pointer',
}
const yesStyle: CSSProperties = { ...btnBase, border: 0, background: 'var(--accent)', color: 'var(--accent-ink)' }
const changeStyle: CSSProperties = {
  ...btnBase, border: '1px solid var(--hairline-12)', background: 'transparent', color: 'var(--text-primary, inherit)',
}
const closeStyle: CSSProperties = {
  position: 'absolute', insetBlockStart: 6, insetInlineEnd: 6, width: 'var(--tap-min, 40px)',
  height: 'var(--tap-min, 40px)', border: 0, background: 'transparent', color: 'var(--text-secondary)',
  fontSize: 18, lineHeight: 1, cursor: 'pointer', borderRadius: 9,
}

export function NearestCityPrompt({ fetchImpl }: { fetchImpl?: typeof fetch } = {}) {
  const { t } = useLocale()
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const [city, setCity] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(false)
  const onHome = HOME_PATHS.has(pathname.replace(/\/+$/, '') || '/')

  // Re-read on every arrival at a home screen, so a city saved through Change
  // (which clears the flag) is reflected when the person comes back.
  useEffect(() => {
    if (!onHome || isDismissed()) {
      setCity(null)
      return
    }
    // Nothing to ask without a session or a configured my-api (dev / unit
    // tests): no read, no async state update.
    if (!loadSession() || !myApiBase()) return
    let cancelled = false
    void loadNearestCityPrompt(fetchImpl ?? fetch).then((c) => {
      if (!cancelled) setCity(c)
    })
    return () => { cancelled = true }
  }, [onHome, pathname, fetchImpl])

  if (!onHome || !city) return null

  const onYes = () => {
    setBusy(true)
    setError(false)
    confirmNearestCity(city, fetchImpl ?? fetch)
      .then(() => setCity(null))
      .catch(() => setError(true))
      .finally(() => setBusy(false))
  }
  const onChange = () => navigate(NEAREST_CITY_EDIT_PATH)
  const onDismiss = () => {
    rememberDismissed()
    setCity(null)
  }

  return (
    <section data-testid="nearest-city-prompt" aria-labelledby="nearest-city-prompt-q" style={cardStyle}>
      <p style={kickerStyle}>{t('cityPrompt.label')}</p>
      <p id="nearest-city-prompt-q" style={{ margin: 0, fontWeight: 700, fontSize: 14 }}>
        {t('cityPrompt.question', { city })}
      </p>
      {error && (
        <p role="alert" data-testid="nearest-city-prompt-error" style={{ margin: 0, color: 'var(--tru-status-amber, var(--warn))', fontSize: 12 }}>
          {t('cityPrompt.error')}
        </p>
      )}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button type="button" data-testid="nearest-city-prompt-yes" onClick={onYes} disabled={busy} style={yesStyle}>
          {busy ? t('cityPrompt.saving') : t('cityPrompt.yes')}
        </button>
        <button type="button" data-testid="nearest-city-prompt-change" onClick={onChange} disabled={busy} style={changeStyle}>
          {t('cityPrompt.change')}
        </button>
      </div>
      <button
        type="button"
        data-testid="nearest-city-prompt-dismiss"
        aria-label={t('cityPrompt.dismiss')}
        onClick={onDismiss}
        disabled={busy}
        style={closeStyle}
      >
        ×
      </button>
    </section>
  )
}
